import apiClient from '@/lib/api/client'

export interface UserActivity {
  id: string
  timestamp: string
  module: string
  action: string
  description?: string
  ip_address?: string
  user_agent?: string
  data?: any
}

export interface LoginHistory {
  id: string
  type: 'success' | 'failed' | 'blocked' | 'logout' | 'session'
  timestamp: string
  ip_address?: string
  user_agent?: string
  status: string
  reason?: string
  location?: string
  device_info?: {
    device: string
    browser: string
    os: string
    user_agent: string
  }
  last_activity?: string
  expires_at?: string
}

export interface ActiveSession {
  id: number
  name: string
  created_at: string
  last_used_at?: string
  expires_at?: string
  is_active: boolean
  abilities: string[]
}

export interface ActivityTimeline {
  [date: string]: {
    id: string
    time: string
    module: string
    action: string
    description: string
    ip_address?: string
    metadata?: any
  }[]
}

export interface ActivityStatistics {
  total_activities: number
  login_count: number
  failed_login_count: number
  logout_count: number
  profile_updates: number
  api_requests: number
  by_module: { [key: string]: number }
  by_action: { [key: string]: number }
  by_date: { [key: string]: number }
  date_range?: {
    start_date: string
    end_date: string
  }
  last_updated?: string
}

export const userActivityService = {
  /**
   * Get all activities for a user
   */
  getUserActivities: async (
    userId: number,
    filters?: {
      module?: string
      action?: string
      start_date?: string
      end_date?: string
      limit?: number
    }
  ): Promise<{ data: UserActivity[]; count: number }> => {
    const params = new URLSearchParams()
    if (filters?.module) params.append('module', filters.module)
    if (filters?.action) params.append('action', filters.action)
    if (filters?.start_date) params.append('start_date', filters.start_date)
    if (filters?.end_date) params.append('end_date', filters.end_date)
    if (filters?.limit) params.append('limit', filters.limit.toString())

    const response = await apiClient.get(
      `/user-management/user-activity/${userId}?${params.toString()}`
    )
    return response.data
  },

  /**
   * Get login history for a user
   */
  getUserLoginHistory: async (
    userId: number,
    filters?: {
      start_date?: string
      end_date?: string
      limit?: number
    }
  ): Promise<{ data: LoginHistory[]; count: number }> => {
    const params = new URLSearchParams()
    if (filters?.start_date) params.append('start_date', filters.start_date)
    if (filters?.end_date) params.append('end_date', filters.end_date)
    if (filters?.limit) params.append('limit', filters.limit.toString())

    const response = await apiClient.get(
      `/user-management/user-activity/${userId}/login-history?${params.toString()}`
    )
    return response.data
  },

  /**
   * Get active sessions for a user
   */
  getActiveSessions: async (userId: number): Promise<{ data: ActiveSession[]; count: number }> => {
    const response = await apiClient.get(`/user-management/user-activity/${userId}/sessions`)
    return response.data
  },

  /**
   * Revoke a user session
   */
  revokeSession: async (userId: number, tokenId: number): Promise<void> => {
    await apiClient.post(`/user-management/user-activity/${userId}/sessions/${tokenId}/revoke`)
  },

  /**
   * Get activity timeline for a user
   */
  getUserTimeline: async (
    userId: number,
    filters?: {
      start_date?: string
      end_date?: string
      limit?: number
    }
  ): Promise<{ data: ActivityTimeline }> => {
    const params = new URLSearchParams()
    if (filters?.start_date) params.append('start_date', filters.start_date)
    if (filters?.end_date) params.append('end_date', filters.end_date)
    if (filters?.limit) params.append('limit', filters.limit.toString())

    const response = await apiClient.get(
      `/user-management/user-activity/${userId}/timeline?${params.toString()}`
    )
    return response.data
  },

  /**
   * Get activity statistics for a user
   */
  getActivityStatistics: async (
    userId: number,
    filters?: {
      start_date?: string
      end_date?: string
    }
  ): Promise<{ data: ActivityStatistics }> => {
    const params = new URLSearchParams()
    if (filters?.start_date) params.append('start_date', filters.start_date)
    if (filters?.end_date) params.append('end_date', filters.end_date)

    const response = await apiClient.get(
      `/user-management/user-activity/${userId}/statistics?${params.toString()}`
    )
    return response.data
  },
}

